import { useEffect, useMemo, useRef, useState } from 'react'
import type { Voyage, VoyageStop } from '@lb/domain'
import { useTranslation } from '@lb/i18n'
import { cn } from '@lb/ui'

import { HIT_R, clusterStops, inDayOrder, labelFor, targetSpans } from './voyage-stops'

/**
 * `.voyage-map` — the year at sea (journey.jsx:238).
 *
 * ── Where the coordinates come from ───────────────────────────────────────
 * Not from the API, by the backend's decision and to our benefit: "a map's
 * geometry is your layout, not our data". The prototype hardcodes an `x`/`y`
 * per stop because it has ten fixed stops; a real member has however many, on
 * whatever days.
 *
 * So a stop's place is its DAY's distance along the path — `day / totalDays` of
 * the curve's length, measured with `getTotalLength`/`getPointAtLength` on the
 * live element. The prototype's own fixture is consistent with this: its stops
 * march left to right in day order along the same curve. What was hand-placed
 * there is computed here, and the result is the same picture for the same data.
 *
 * Measuring rather than solving the Bézier in JS is deliberate — the browser
 * already has an exact implementation, and a hand-rolled one would be a second
 * definition of the path to keep in step with the `d` attribute.
 *
 * ── `today` is not clamped, but the boat is ───────────────────────────────
 * A member past their year-mark reports day 400 of 365. The number is theirs
 * and renders as sent; the geometry clamps, or the boat sails off the end.
 */

/* The prototype's viewBox and its single sinuous path, verbatim. */
const W = 1200
const H = 460
const PATH_D = `
  M 30 260
  C 130 180, 240 180, 345 215
  S 540 320, 660 250
  S 880 160, 1170 235
`

type Placed = VoyageStop & { x: number; y: number }

/**
 * What a tap selects: one tap target's stops. `id` is the index of its first
 * stop in day order, which is stable for as long as the voyage data is.
 */
export type StopSelection = { id: number; stops: VoyageStop[] }

const INSET = 0.04
const atRatio = (ratio: number) => INSET + ratio * (1 - INSET * 2)

export function VoyageMap({
  voyage,
  activeId,
  onSelect,
}: {
  voyage: Voyage
  /** The selected tap target's `id`, or null. */
  activeId: number | null
  onSelect: (selection: StopSelection | null) => void
}) {
  const { t } = useTranslation()
  const path = useRef<SVGPathElement>(null)
  const [length, setLength] = useState<number | null>(null)

  /*
   * Measured after mount, because `getTotalLength` needs a laid-out element.
   *
   * One measurement, not one per render: the path is a constant, so its length
   * cannot change — not with the viewport, since the `viewBox` scales rather
   * than reflows.
   */
  useEffect(() => {
    if (path.current) setLength(path.current.getTotalLength())
  }, [])

  const progress = Math.min(voyage.today, voyage.totalDays) / voyage.totalDays

  /*
   * Days map into the MIDDLE of the path, not the whole of it.
   *
   * Day 1 at 0% would sit exactly on the curve's start point, where its label
   * runs off the left edge of the viewBox and there is no room for the marker's
   * stroke. The design solves it by hand — its path starts at x=30 and its
   * day-1 stop is drawn at x=60, its day-365 stop at 1120 against an end of
   * 1170. `INSET` is that same margin, expressed once.
   */
  const placed = useMemo<Placed[]>(() => {
    if (!length || !path.current) return []
    return inDayOrder(voyage.stops).map((stop) => {
      const ratio = Math.min(Math.max(stop.day, 1), voyage.totalDays) / voyage.totalDays
      const point = path.current!.getPointAtLength(atRatio(ratio) * length)
      return { ...stop, x: point.x, y: point.y }
    })
  }, [length, voyage.stops, voyage.totalDays])

  /*
   * Which stops get a label drawn ON the map.
   *
   * The rule is where you came aboard, where you are, and where the year ends
   * — the design's own days 1/47/184/365 are its fixture's numbers and would
   * label nothing for a real member.
   *
   * ── And then they must not collide ───────────────────────────────────────
   * Day is mapped to x along the path, so early in a member's year "joined"
   * and "current" land within a few pixels of each other and their two-line
   * labels print on top of one another. Measured on `parent1` at day 3: an
   * unreadable stack of four overlapping strings.
   *
   * So it is a pass in priority order — current first, because that is the one
   * the screen is about — dropping any candidate that lands within `MIN_GAP`
   * of a label already placed. A dropped stop keeps its dot and its tap
   * target; only the printed name goes, which is what the map's own help text
   * ("tap any stop to read about it") already promises.
   */
  /*
   * The ONE stop that is "here".
   *
   * `state` is not that stop — measured on `parent1`, the API returns **twenty**
   * stops marked `current`, because every stop landing on today's day carries
   * that state. Drawing the current treatment off `state` therefore gave twenty
   * enlarged markers and twenty pulsing halos, which says "you are here" twenty
   * times and so says it nowhere.
   *
   * The map's own claim is narrower than the API's: exactly one marker is where
   * the boat is moored. The LAST current stop is that one — stops arrive in day
   * order, so the last of today's is the furthest along.
   *
   * Identified by INDEX, not by `day`-`key`. That pair is not unique: eight of
   * this member's stops share both, so keying on it matched all eight and drew
   * eight haloes stacked at one point. The same non-uniqueness was feeding
   * React's `key`, which is the other half of the bug.
   */
  const hereIndex = useMemo(() => {
    let found = -1
    placed.forEach((stop, i) => {
      if (stop.state === 'current') found = i
    })
    return found
  }, [placed])

  const labelled = useMemo<Placed[]>(() => {
    const MIN_GAP = 150
    const candidates = [
      hereIndex >= 0 ? placed[hereIndex] : undefined,
      placed.find((s) => s.key === 'yearMark'),
      placed.find((s) => s.key === 'joined'),
    ].filter((s): s is Placed => Boolean(s))

    const kept: Placed[] = []
    for (const stop of candidates) {
      if (kept.some((k) => Math.abs(k.x - stop.x) < MIN_GAP)) continue
      kept.push(stop)
    }
    return kept
  }, [placed, hereIndex])

  /*
   * One tap target per group of stops too close to tell apart — see
   * `clusterStops`. Each carries the index of its first stop, which is the
   * selection's id.
   */
  const targets = useMemo(() => {
    const clusters = clusterStops(placed)
    const spans = targetSpans(clusters)
    let start = 0
    return clusters.map((stops, i) => {
      const target = { id: start, stops, ...spans[i]! }
      start += stops.length
      return target
    })
  }, [placed])
  const activeIndexes = useMemo(() => {
    const target = targets.find((c) => c.id === activeId)
    return new Set(target ? target.stops.map((_, i) => target.id + i) : [])
  }, [targets, activeId])

  const boat = useMemo(() => {
    if (!length || !path.current) return null
    /* The same inset, so the boat lands on the current stop rather than beside
       it — two different mappings would show a boat that has sailed past the
       island it is moored at. */
    return path.current.getPointAtLength(atRatio(progress) * length)
  }, [length, progress])

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        className="w-full"
        /*
         * `group`, not `img`. An `img` role makes every child presentational,
         * so the stop buttons inside it were not in the accessibility tree at
         * all — the map announced itself as a button board and had none.
         */
        role="group"
        aria-label={t('voyage.mapLabel', { day: voyage.today, total: voyage.totalDays })}
      >
        <defs>
          {/*
           * Sky and sun as TOKENS at the design's opacities.
           *
           * The prototype's three cool greys deepen a cream page toward the
           * horizon. Over the dark canvas the same three literals read as a
           * grey smudge — lighter than what is under them, so the haze
           * inverts. `--slate` is the same relationship stated once: a cool
           * veil that darkens a light canvas and lifts a dark one.
           */}
          <linearGradient id="lb-sky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--slate)" stopOpacity="0" />
            <stop offset="60%" stopColor="var(--slate)" stopOpacity="0.1" />
            <stop offset="100%" stopColor="var(--slate)" stopOpacity="0.2" />
          </linearGradient>
          <radialGradient id="lb-sun" cx="14%" cy="22%" r="14%">
            <stop offset="0%" stopColor="var(--rose-light)" stopOpacity="0.7" />
            <stop offset="100%" stopColor="var(--rose-light)" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/*
         * Everything drawn before the tap targets is decoration, and says so:
         * `pointer-events: none` on every layer, so a label, the boat or the
         * « You are here » flag can never sit on top of a stop and swallow its
         * tap — which is what they did, being drawn after the stops.
         */}
        <g pointerEvents="none" aria-hidden>
        <rect x="0" y="0" width={W} height={H} fill="url(#lb-sky)" />
        <circle cx="160" cy="100" r="120" fill="url(#lb-sun)" />

        {/* Ocean, then two distant islands. Decorative, and `aria-hidden` by
            sitting inside an `img` role that already has its label. */}
        <g stroke="var(--slate)" strokeWidth="0.7" fill="none" opacity="0.3">
          <path d={`M 0 380 Q ${W / 4} 372, ${W / 2} 380 T ${W} 380`} />
          <path d={`M 0 405 Q ${W / 4} 395, ${W / 2} 405 T ${W} 405`} />
          <path d={`M 0 430 Q ${W / 4} 418, ${W / 2} 430 T ${W} 430`} />
        </g>
        {/* Distant islands. `--ink` rather than the design's literal navy: on
            the dark canvas a pinned #293339 at 16% is nothing at all. */}
        <g opacity="0.16" fill="var(--ink)">
          <ellipse cx="220" cy="350" rx="60" ry="6" />
          <path d="M 180 350 Q 220 326, 260 350 Z" />
          <ellipse cx="900" cy="345" rx="80" ry="7" />
          <path d="M 845 345 Q 900 318, 955 345 Z" />
        </g>

        {/* The whole voyage, dashed. */}
        <path
          ref={path}
          d={PATH_D}
          stroke="var(--slate)"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          strokeDasharray="3 8"
          opacity="0.55"
        />
        {/* The part already sailed. `pathLength="100"` normalises the dash to a
            percentage so it does not depend on the curve's real length. */}
        <path
          d={PATH_D}
          stroke="var(--progress)"
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
          pathLength="100"
          strokeDasharray={`${progress * 100} 100`}
        />

        {placed.map((stop, index) => (
          <Stop
            key={`${stop.day}-${stop.key}-${index}`}
            stop={stop}
            here={index === hereIndex}
            active={activeIndexes.has(index)}
          />
        ))}

        {/*
         * Labels for the stops worth naming on the map itself.
         *
         * The design labels days 1, 47, 184 and 365 — but those are ITS
         * fixture's numbers, and hardcoding them would label nothing for a real
         * member. The rule underneath them is what generalises: where you came
         * aboard, where you are, and where the year ends. Everything else is a
         * tap away, which is what the map's own help text says.
         */}
        {labelled.map((stop) => {
          /*
           * All three label BELOW, including the current one — the design
           * puts its day-184 label above, but that space is taken here by the
           * boat and the "you are here" flag, which the prototype's fixture
           * happened to place elsewhere.
           *
           * `currentColor` rather than the design's literals: the map's
           * backdrop is the page canvas, which FLIPS. A pinned `#293339`
           * label is invisible on the dark theme, which is what it did.
           */
          return (
            <g
              key={`label-${stop.day}`}
              transform={`translate(${stop.x}, ${stop.y})`}
              aria-hidden
              textAnchor={anchorFor(stop.x)}
            >
              <text
                x={edgeNudge(stop.x)}
                y={30}
                fontSize="10.5"
                fontWeight="700"
                letterSpacing="0.04em"
                fill="currentColor"
                className="text-ink"
              >
                {t('voyage.day', { day: stop.day })}
              </text>
              <text
                x={edgeNudge(stop.x)}
                y={44}
                fontSize="13"
                fill="currentColor"
                className="text-ink"
              >
                {truncate(labelFor(stop, t))}
              </text>
            </g>
          )
        })}

        {boat ? (
          <>
            {/*
             * Two nested groups on purpose: the outer one carries the position
             * (which depends on how far the member has sailed) and the inner
             * one rocks. The prototype bakes its translate into the keyframes
             * because its boat never moves; ours does, and a keyframe that
             * re-declares `transform` would throw the position away.
             */}
            <g transform={`translate(${boat.x}, ${boat.y - 22})`} aria-hidden>
              <g className="vm-boat-rock">
                <path
                  d="M -16 6 L 16 6 L 12 16 L -12 16 Z"
                  fill="var(--cream)"
                  stroke="var(--ink)"
                  strokeWidth="1.4"
                  strokeLinejoin="round"
                />
                <line
                  x1="0"
                  y1="6"
                  x2="0"
                  y2="-16"
                  stroke="var(--ink)"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
                <path
                  d="M 0 -14 L 0 2 L 14 -6 Z"
                  fill="var(--rose)"
                  stroke="var(--ink)"
                  strokeWidth="1.2"
                />
                <circle cx="0" cy="-16" r="2" fill="var(--ink)" />
              </g>
            </g>
            <g transform={`translate(${boat.x}, ${boat.y - 70})`} aria-hidden>
              <rect x="-52" y="-13" width="104" height="22" rx="11" fill="var(--terra)" />
              <text
                x="0"
                y="3"
                textAnchor="middle"
                fontSize="10.5"
                fontWeight="700"
                fill="var(--ink-on-tint)"
                letterSpacing="0.1em"
              >
                {t('voyage.youAreHere').toUpperCase()}
              </text>
            </g>
          </>
        ) : null}
        </g>

        {/*
         * The tap targets, LAST so nothing is drawn over them, one per group
         * of stops (`clusterStops`). A capsule spanning the group
         * (`targetSpans` — never over a neighbour), transparent but painted
         * so it takes the pointer. Each is a real control: focusable, named
         * for what the card will show, and pressed while its card is open.
         */}
        {targets.map((target) => {
          const ys = target.stops.map((s) => s.y)
          const { x0, x1 } = target
          const y0 = Math.min(...ys) - HIT_R
          const y1 = Math.max(...ys) + HIT_R
          const first = target.stops[0]!
          const lastDay = target.stops[target.stops.length - 1]!.day
          const active = activeId === target.id
          const select = () => onSelect(active ? null : { id: target.id, stops: target.stops })
          const label =
            target.stops.length === 1
              ? t('voyage.stopLabel', { day: first.day, label: labelFor(first, t) })
              : first.day === lastDay
                ? t('voyage.clusterLabelDay', { count: target.stops.length, day: first.day })
                : t('voyage.clusterLabelDays', { count: target.stops.length, from: first.day, to: lastDay })
          return (
            <rect
              key={`hit-${target.id}`}
              x={x0}
              y={y0}
              width={x1 - x0}
              height={y1 - y0}
              rx={Math.min(HIT_R, (x1 - x0) / 2)}
              fill="transparent"
              pointerEvents="all"
              role="button"
              tabIndex={0}
              aria-label={label}
              aria-pressed={active}
              className="cursor-pointer outline-none focus-visible:stroke-[var(--ink)] focus-visible:[stroke-width:2.5]"
              onClick={select}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  select()
                }
              }}
            />
          )
        })}
      </svg>

      {/*
       * `.vm-help` — and the verb depends on the pointer, not on the mock.
       *
       * The prototype says "Click any stop"; ours said "Tap". Both are right
       * half the time and the mock only ever ran on a desktop, while iOS Safari
       * is a first-class target here (§2). So the verb is chosen by
       * `pointer: fine` rather than picked once: a mouse gets the design's own
       * word, a finger gets the true one. Both strings are translated, and only
       * one is in the accessibility tree at a time because the other is
       * `display: none` rather than merely transparent.
       */}
      <p className="mt-2 text-center text-[12px] text-muted">
        <span className="hidden [@media(pointer:fine)]:inline">{t('voyage.helpClick')}</span>
        <span className="[@media(pointer:fine)]:hidden">{t('voyage.help')}</span>
      </p>
    </div>
  )
}

/**
 * `.vm-stop` — one island, drawn only.
 *
 * The control is NOT here. It used to be — a transparent circle per stop —
 * and the next stop's marker, the labels and the boat were all drawn over it.
 * The map's tap targets are a layer of their own on top of everything (see
 * `VoyageMap`), and this is decoration beneath them.
 */
function Stop({
  stop,
  here,
  active,
}: {
  stop: Placed
  /** The one stop the boat is moored at — not every stop the API calls current. */
  here: boolean
  active: boolean
}) {
  const r = here ? 12 : 9

  /*
   * ── Tokens, not the design's literals ────────────────────────────────────
   * The prototype is a light-theme artefact and its hex values encode that. A
   * milestone drawn at a pinned `#293339` is a dark dot on a dark canvas —
   * invisible — while an upcoming stop pinned to `#F7F2EA` became the
   * BRIGHTEST thing on the dark map, which is exactly backwards: those are the
   * stops that have not happened.
   *
   * `--ink` and `--cream` flip together, so "solid" and "hollow" keep meaning
   * solid and hollow in both themes. That is the relationship the design is
   * expressing; the hex values were only ever one rendering of it.
   */
  const fill =
    stop.state === 'done'
      ? stop.kind === 'milestone'
        ? 'var(--ink)'
        : 'var(--terra)'
      : here
        ? 'var(--vert)'
        : 'var(--cream)'

  const stroke = stop.state === 'upcoming' ? 'var(--slate)' : 'var(--ink)'

  /* The glyph rides on the marker's fill, so it takes that fill's counterpart:
     cream on ink (both flip), and `--ink-on-tint` on the doré / vert tints,
     which do not flip. */
  const glyphFill = stop.kind === 'milestone' ? 'var(--cream)' : 'var(--ink-on-tint)'

  return (
    <g
      transform={`translate(${stop.x}, ${stop.y})`}
      className={cn('vm-stop', active && 'opacity-100')}
      aria-hidden
    >
      {/*
       * `.vm-pulse` — a halo breathing out of the CURRENT stop and fading.
       * It was a static ring at 55% opacity, which drew the same shape and
       * said none of it: the animation is what makes the marker read as
       * "here, now" rather than as one more decorated dot.
       */}
      {here ? (
        <circle
          className="vm-pulse"
          r={r + 8}
          fill="none"
          stroke="var(--vert)"
          strokeWidth="1.5"
          opacity="0.55"
        />
      ) : null}
      {active ? <circle r={r + 5} fill="none" stroke="var(--ink)" strokeWidth="2" /> : null}

      <circle
        className="vm-dot"
        r={r}
        fill={fill}
        stroke={stroke}
        strokeWidth={stop.state === 'upcoming' ? 1.5 : 1.6}
        strokeDasharray={stop.state === 'upcoming' ? '2 3' : undefined}
      />

      {/* The kind glyph, drawn only where the fill is dark enough to carry it. */}
      {stop.state !== 'upcoming' ? <Glyph kind={stop.kind} fill={glyphFill} /> : null}
      {stop.kind === 'challenge' ? (
        <rect x="-3" y="-3" width="6" height="6" fill="var(--ink)" opacity="0.5" />
      ) : null}
    </g>
  )
}

/** Exported so the legend draws the very same marks the map does. */
export function Glyph({ kind, fill }: { kind: VoyageStop['kind']; fill: string }) {
  if (kind === 'course') return <path d="M -3 -4 L 4 0 L -3 4 Z" fill={fill} />
  if (kind === 'milestone')
    return (
      <path
        d="M 0 -5 L 1.5 -1.5 L 5 0 L 1.5 1.5 L 0 5 L -1.5 1.5 L -5 0 L -1.5 -1.5 Z"
        fill={fill}
      />
    )
  if (kind === 'moment')
    return (
      <path
        d="M 0 4 C -3 1, -4.5 -1, -3 -2.5 C -1.8 -3.5, 0 -2.5, 0 -1.5 C 0 -2.5, 1.8 -3.5, 3 -2.5 C 4.5 -1, 3 1, 0 4 Z"
        fill={fill}
      />
    )
  return null
}

/*
 * SVG text is not clipped by its parent and has no ellipsis, so a centred label
 * near either end runs off the viewBox. These two shift it inward instead of
 * truncating it further: anchor to the near edge, and nudge back from it.
 */
function anchorFor(x: number): 'start' | 'middle' | 'end' {
  if (x < 140) return 'start'
  if (x > W - 140) return 'end'
  return 'middle'
}

function edgeNudge(x: number): number {
  if (x < 140) return -12
  if (x > W - 140) return 12
  return 0
}

/* SVG text does not wrap and has no ellipsis. The design truncates at 30 too. */
function truncate(label: string): string {
  return label.length > 30 ? `${label.slice(0, 28)}…` : label
}
